import type { GameState, SmeltItem } from '../state';
import { D, add, sub, gte, mul, pow, type Decimal } from '../math/num';
import { ALLOY_YIELD, SMELT_MS, STORAGE_PER_RANK, getSmeltUpgrade } from '../data/smelter';
import { MECH_CORE_INSTANCE_ID } from '../data/mechCore';
import type { Rarity } from '../data/parts/partTypes';

/**
 * Smelter sim (data/smelter.ts holds the numbers). The queue melts off
 * elapsedMs one part per SMELT_MS, so it runs identically online and offline.
 */

export interface SmelterOpResult {
  ok: boolean;
  reason?: string;
  queued?: number;
}

/** Alloy a single queued item pours. */
export function itemYield(item: SmeltItem): Decimal {
  return D(ALLOY_YIELD[item.rarity] + Math.max(0, item.level - 1));
}

/** Process the melt queue (called from stepWorld). */
export function stepSmelter(state: GameState, _dtMs: number): void {
  const sm = state.smelter;
  if (!sm.unlocked || sm.queue.length === 0) return;
  while (sm.queue.length > 0 && state.elapsedMs >= sm.nextDoneMs) {
    const item = sm.queue.shift()!;
    state.wallet.alloy = add(state.wallet.alloy, itemYield(item));
    sm.smelted += 1;
    sm.nextDoneMs += SMELT_MS;
  }
}

/** DEV/admin: finish the whole queue right now. */
export function drainSmelter(state: GameState): void {
  const sm = state.smelter;
  if (sm.queue.length === 0) return;
  sm.nextDoneMs = state.elapsedMs - SMELT_MS * (sm.queue.length - 1);
  stepSmelter(state, 0);
}

/** Spare = in the stash, not wired into the Mind, not the Core. */
function spareIds(state: GameState): Set<string> {
  const equipped = new Set(state.mind.nodes.map((n) => n.instanceId));
  const prot = new Set(state.inventory.protectedIds);
  const ids = new Set<string>();
  for (const p of state.inventory.parts) {
    if (p.instanceId !== MECH_CORE_INSTANCE_ID && !equipped.has(p.instanceId) && !prot.has(p.instanceId)) ids.add(p.instanceId);
  }
  return ids;
}

/** Queue specific parts for melting. Removes them from the stash immediately
 *  (that is the inventory-pressure relief). */
export function queueSmelt(state: GameState, instanceIds: string[]): SmelterOpResult {
  const sm = state.smelter;
  if (!sm.unlocked) return { ok: false, reason: 'The Smelter is not online.' };
  const spares = spareIds(state);
  const wanted = new Set(instanceIds.filter((id) => spares.has(id)));
  // GOATED parts are far too rare to ever be "spare" — never melt them.
  for (const p of state.inventory.parts) if (p.rarity === 'goated') wanted.delete(p.instanceId);
  if (wanted.size === 0) return { ok: false, reason: 'Nothing spare to melt (equipped, GOATED, and the Core never melt).' };

  const wasEmpty = sm.queue.length === 0;
  const keep = [];
  for (const p of state.inventory.parts) {
    if (wanted.has(p.instanceId)) {
      sm.queue.push({ defId: p.defId, rarity: p.rarity, level: p.level });
    } else {
      keep.push(p);
    }
  }
  state.inventory.parts = keep;
  if (wasEmpty) sm.nextDoneMs = state.elapsedMs + SMELT_MS;
  return { ok: true, queued: wanted.size };
}

/** The bulk-scrap QoL: melt every spare of a rarity in one click. */
export function smeltAllOfRarity(state: GameState, rarity: Rarity): SmelterOpResult {
  if (rarity === 'goated') return { ok: false, reason: 'GOATED parts are never melted.' };
  const equipped = new Set(state.mind.nodes.map((n) => n.instanceId));
  const prot = new Set(state.inventory.protectedIds);
  const ids = state.inventory.parts
    .filter((p) => p.rarity === rarity && p.instanceId !== MECH_CORE_INSTANCE_ID && !equipped.has(p.instanceId) && !prot.has(p.instanceId))
    .map((p) => p.instanceId);
  return queueSmelt(state, ids);
}

export function smeltUpgradeCost(state: GameState, id: string): Decimal | null {
  const def = getSmeltUpgrade(id);
  if (!def) return null;
  const rank = state.smelter.upgrades[id] ?? 0;
  return mul(D(def.baseCost), pow(D(def.costGrowth), rank));
}

/** Buy a permanent capacity rank with Alloy. */
export function buySmeltUpgrade(state: GameState, id: string): SmelterOpResult {
  const sm = state.smelter;
  if (!sm.unlocked) return { ok: false, reason: 'The Smelter is not online.' };
  const def = getSmeltUpgrade(id);
  if (!def) return { ok: false, reason: 'Unknown upgrade.' };
  const rank = sm.upgrades[id] ?? 0;
  if (def.maxRank > 0 && rank >= def.maxRank) return { ok: false, reason: 'Fully ranked.' };
  const cost = smeltUpgradeCost(state, id)!;
  if (!gte(state.wallet.alloy, cost)) return { ok: false, reason: 'Not enough Alloy.' };
  state.wallet.alloy = sub(state.wallet.alloy, cost);
  sm.upgrades[id] = rank + 1;
  if (id === 'su.racks') {
    state.resources.storage += STORAGE_PER_RANK;
    state.inventory.cap += STORAGE_PER_RANK;
  }
  // su.ballast and su.bay are read live by weightCapacity / maxDrones.
  return { ok: true };
}

/** Bring the Smelter online — rides the Sir Cumference kill. */
export function unlockSmelter(state: GameState): void {
  if (state.smelter.unlocked) return;
  state.smelter.unlocked = true;
  state.meta.unlocks.smelter = true;
}
